import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import type { Guardian } from '@/types/domain';
import type { GuardianPayload } from '@/services/api/endpoints/guardians';

const schema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  middleName: z.string().max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  // Required, unlike a student's: a guardian exists to be contacted, and a
  // record with no way to reach them is of no use in an emergency.
  phone: z.string().min(1, 'A phone number is required').max(32),
  alternatePhone: z.string().max(32),
  email: z.string().email('Enter a valid e-mail address').or(z.literal('')),
  occupation: z.string().max(150),
  employer: z.string().max(200),
  nationality: z.string().max(100),
  nationalId: z.string().max(100),
});

export type GuardianFormValues = z.infer<typeof schema>;

const EMPTY: GuardianFormValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  phone: '',
  alternatePhone: '',
  email: '',
  occupation: '',
  employer: '',
  nationality: '',
  nationalId: '',
};

const blankToUndefined = (value: string) => (value === '' ? undefined : value);

export function toPayload(values: GuardianFormValues): GuardianPayload {
  return {
    firstName: values.firstName,
    middleName: blankToUndefined(values.middleName),
    lastName: values.lastName,
    phone: values.phone,
    alternatePhone: blankToUndefined(values.alternatePhone),
    email: blankToUndefined(values.email),
    occupation: blankToUndefined(values.occupation),
    employer: blankToUndefined(values.employer),
    nationality: blankToUndefined(values.nationality),
    nationalId: blankToUndefined(values.nationalId),
  };
}

export function toFormValues(guardian: Guardian): GuardianFormValues {
  return {
    firstName: guardian.firstName,
    middleName: guardian.middleName ?? '',
    lastName: guardian.lastName,
    phone: guardian.phone,
    alternatePhone: guardian.alternatePhone ?? '',
    email: guardian.email ?? '',
    occupation: guardian.occupation ?? '',
    employer: guardian.employer ?? '',
    nationality: guardian.nationality ?? '',
    nationalId: guardian.nationalId ?? '',
  };
}

interface GuardianFormModalProps {
  open: boolean;
  /** Null when adding. */
  guardian: Guardian | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (
    values: GuardianFormValues,
    setError: ReturnType<typeof useForm<GuardianFormValues>>['setError'],
  ) => void;
}

export function GuardianFormModal({ open, guardian, saving, onClose, onSubmit }: GuardianFormModalProps) {
  const form = useForm<GuardianFormValues>({
    resolver: zodResolver(schema),
    values: guardian === null ? EMPTY : toFormValues(guardian),
  });

  const { register } = form;
  const submit = form.handleSubmit((values) => onSubmit(values, form.setError));
  const { errors } = form.formState;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={guardian === null ? 'Add guardian' : 'Edit guardian'}
      description={
        guardian === null
          ? 'One record serves every child this person is responsible for. Check whether they already exist before adding them again.'
          : 'This change reaches every child linked to this guardian.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="guardian-form" loading={saving}>
            {guardian === null ? 'Add guardian' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form id="guardian-form" className="space-y-4" noValidate onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField label="First name" required {...register('firstName')} error={errors.firstName?.message} />
          <TextField label="Middle name" {...register('middleName')} error={errors.middleName?.message} />
          <TextField label="Last name" required {...register('lastName')} error={errors.lastName?.message} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Phone"
            required
            {...register('phone')}
            error={errors.phone?.message}
            hint="The number the school will call first"
          />
          <TextField
            label="Alternate phone"
            {...register('alternatePhone')}
            error={errors.alternatePhone?.message}
          />
        </div>

        <TextField
          label="E-mail address"
          type="email"
          {...register('email')}
          error={errors.email?.message}
          hint="Identifies the guardian within the school, so registering a sibling reuses this record"
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Occupation" {...register('occupation')} error={errors.occupation?.message} />
          <TextField label="Employer" {...register('employer')} error={errors.employer?.message} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Nationality" {...register('nationality')} error={errors.nationality?.message} />
          <TextField label="National ID" {...register('nationalId')} error={errors.nationalId?.message} />
        </div>
      </form>
    </Modal>
  );
}
